import { FUN_MIN_DEPOSIT_FLOOR_USD } from "@decibeltrade/utils";
import { z } from "zod/v4";

import packageJson from "../package.json";

const envSchema = z.object({
  NEXT_PUBLIC_API_KEY: z.string().min(1),
  SERVER_API_KEY: z.string().min(1).optional(), // Optional because it's only available on the server
  SERVER_INTERNAL_ORG_ID: z.string().optional(), // Geomi internal organization ID for server-to-server auth
  SERVER_INTERNAL_PROJECT_ID: z.string().optional(), // Geomi internal project ID for server-to-server auth
  SERVER_INTERNAL_APP_ID: z.string().optional(), // Geomi internal application ID for server-to-server auth
  SERVER_SOLANA_CLAIM_SIGNER_KEY: z.string().optional(),
  NEXT_PUBLIC_SDK_CONFIG_NAME: z.enum(["testnet", "mainnet", "docker", "local"]),
  NEXT_PUBLIC_GAS_STATION_API_KEY: z.string().min(1).optional(), // Aptos Labs Gas Station API key
  NEXT_PUBLIC_GAS_STATION_ADDRESS: z.string().min(1).optional(), // Gas station fee-payer address (required to submit encrypted txns)
  // Point FFT at a throwaway campaign deploy (see .context/fft-testnet-deploy/).
  NEXT_PUBLIC_FFT_CAMPAIGN_PACKAGE: z.string().min(1).optional(),
  NEXT_PUBLIC_FFT_CAMPAIGN_ADDR: z.string().min(1).optional(),
  // Local trading-api override (FFT smoke rig, see .context/fft-testnet-deploy/).
  NEXT_PUBLIC_TRADING_HTTP_URL: z.string().min(1).optional(),
  NEXT_PUBLIC_TRADING_WS_URL: z.string().min(1).optional(),
  NEXT_PUBLIC_SENTRY_ENVIRONMENT: z.enum(["development", "production"]).optional(),
  NEXT_PUBLIC_GOOGLE_CLIENT_ID: z.string().min(1).optional(),
  NEXT_PUBLIC_API_URL: z.string().min(1).optional(),
  NEXT_PUBLIC_MAINTENANCE_MODE: z.stringbool().optional(),
  NEXT_PUBLIC_TRADE_MAINTENANCE_MODE: z.stringbool().optional(),
  NEXT_PUBLIC_ENCRYPTION_KILLSWITCH: z.stringbool().optional(), // Hard off-switch for encrypted txns; wins over Statsig. For when Statsig itself is unavailable.
  NEXT_PUBLIC_ENCRYPTION_LOCAL_OVERRIDE: z.stringbool().optional(), // Local-dev only (ignored unless VERCEL_ENV is unset): force this device into the encryption cohort so the indicator/encrypted path can be exercised without a live Statsig config.
  NEXT_PUBLIC_PRE_DEPOSITS_CAMPAIGN_END_LEDGER_VERSION: z.string().regex(/^\d+$/).optional(), // Ledger version at campaign close; when set, predeposit view calls are pinned to this version and cached in localStorage
  NEXT_PUBLIC_VERCEL_ENV: z.enum(["development", "preview", "production"]).optional(),
  NEXT_DEV_COUNTRY_CODE: z.string().optional(), // Server-only, dev-only optional override for country detection
  NEXT_DEV_REGION_CODE: z.string().optional(), // Server-only, dev-only optional override for region detection
  NODE_ENV: z.enum(["development", "production"]).optional(),
  CI: z.string().optional(),
  GA_ID: z.string().optional(),
  VERCEL_URL: z.string().optional(),
  PORT: z.string().optional(),
  NEXT_PUBLIC_MESH_CONNECT_URL: z.string().optional(), // Mesh Connect API URL (feature disabled if not set)
  NEXT_PUBLIC_MESH_CONNECT_CLIENT_ID: z.string().optional(), // Mesh Connect client ID (feature disabled if not set)
  NEXT_PUBLIC_MESH_COINBASE_INTEGRATION_ID: z.string().optional(), // Override Mesh Coinbase integration ID (defaults to COINBASE_SANDBOX; set to MESH_INTEGRATION_ID.COINBASE value in prod)
  NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY: z.string().optional(),
  NEXT_PUBLIC_RUDDERSTACK_DATA_PLANE_URL: z.string().optional(),
  NEXT_PUBLIC_SUI_RPC_URL: z.string().optional(), // SUI RPC URL for cross-chain operations
  NEXT_PUBLIC_ENABLE_SUI_WALLET: z.stringbool().optional(), // Enable Sui wallet in wallet selector (defaults to false)
  NEXT_PUBLIC_HIDDEN_MARKET_ADDRS: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) return [];
      try {
        return JSON.parse(val) as string[];
      } catch {
        console.warn("[env] Failed to parse NEXT_PUBLIC_HIDDEN_MARKET_ADDRS, falling back to []");
        return [];
      }
    })
    .pipe(z.array(z.string())), // JSON array of market addresses to hide from the UI (e.g. '["0xabc...","0xdef..."]')
  NEXT_PUBLIC_APP_VERSION: z.string(), // App version (defaults to package.json version)
  NEXT_PUBLIC_TRADE_ALLOWED_CHECKS_ENABLED: z.stringbool().optional(),
  NEXT_PUBLIC_ENABLE_KEYLESS: z.stringbool().optional(),
  NEXT_PUBLIC_ENABLE_MESH_WITHDRAW: z.stringbool().optional(), // Enable Mesh CEX off-ramp (defaults to false)
  NEXT_PUBLIC_ENABLE_AGENT_MODE: z.stringbool().optional(), // True forces UI availability; false or unset uses Statsig.
  NEXT_PUBLIC_ENABLE_IDEAS: z.stringbool().optional(), // Enable Ideas: the /feed page, the Discover tab, and posting from the order form and positions (defaults to false)
  NEXT_PUBLIC_ENABLE_RWA_INSIGHTS: z.stringbool().optional(), // Enable RWA Insights tab + after-hours stat (defaults to false)
  NEXT_PUBLIC_ENABLE_MARKET_CONTEXTS_WS: z.stringbool().optional(), // Live 24h figures over the all_market_contexts WS topic (defaults to false)
  NEXT_PUBLIC_ENABLE_RWA_AFTER_HOURS: z.stringbool().optional(), // Enable the after-hours quote poll + toolbar moon stat (defaults to false). Mirror the BFF's MASSIVE_AFTER_HOURS_QUOTE_ENABLED: while that is paused the endpoint only returns a hidden "closed" quote, so keep this off to avoid a poll that can never render.
  // Rpc URLS Override
  NEXT_PUBLIC_SOLANA_RPC_URL: z.string().optional(),
  NEXT_PUBLIC_APTOS_RPC_URL: z.string().optional(),
  NEXT_PUBLIC_ARBITRUM_RPC_URL: z.string().optional(),
  NEXT_PUBLIC_BASE_RPC_URL: z.string().optional(),
  NEXT_PUBLIC_ETHEREUM_RPC_URL: z.string().optional(),
  NEXT_PUBLIC_ANKR_API_KEY: z.string().optional(), // Ankr Advanced API key for ankr_getTransactionsByAddress
  NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID: z
    .string()
    .optional()
    .transform((val) => (val === "" ? undefined : val)),
  NEXT_PUBLIC_AUTO_IMPORT_TRANSFERS_ENABLED: z.stringbool().optional(), // Feature flag for auto-importing pending CCTP transfers on mount (defaults to disabled)
  NEXT_PUBLIC_SOLANA_CLAIM_SERVER_URL: z.string().optional(),
  // Canonical hostname to embed in the SIWA message's `domain` field. When
  // set, every client signs the same domain regardless of which host the
  // page was loaded from (apex vs www, canonical vs Vercel alias, PWA
  // install origin, etc.). Without it we fall back to `window.location.hostname`,
  // which produces silent 401 "SIWA domain mismatch" errors whenever the
  // browser URL drifts from whatever single hostname the backend has
  // configured as `FRONTEND_DOMAIN`.
  NEXT_PUBLIC_SIWA_DOMAIN: z.string().optional(),
  NEXT_PUBLIC_FUN_API_KEY: z.string().optional(), // Fun (fun.xyz) publishable Checkout SDK key; feature disabled if not set
  // Deliberately no default: see `isFunDepositsConfigured` below.
  NEXT_PUBLIC_FUN_DEPOSIT_MIN_USD: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .optional()
    .transform((val) => (val === undefined ? undefined : Number(val))),
});

export const env = envSchema.parse({
  NEXT_PUBLIC_API_KEY: process.env.NEXT_PUBLIC_API_KEY,
  SERVER_API_KEY: process.env.SERVER_API_KEY,
  SERVER_INTERNAL_ORG_ID: process.env.SERVER_INTERNAL_ORG_ID,
  SERVER_INTERNAL_PROJECT_ID: process.env.SERVER_INTERNAL_PROJECT_ID,
  SERVER_INTERNAL_APP_ID: process.env.SERVER_INTERNAL_APP_ID,
  SERVER_SOLANA_CLAIM_SIGNER_KEY: process.env.SERVER_SOLANA_CLAIM_SIGNER_KEY,
  NEXT_PUBLIC_SDK_CONFIG_NAME: process.env.NEXT_PUBLIC_SDK_CONFIG_NAME,
  NEXT_PUBLIC_GAS_STATION_API_KEY: process.env.NEXT_PUBLIC_GAS_STATION_API_KEY,
  NEXT_PUBLIC_GAS_STATION_ADDRESS: process.env.NEXT_PUBLIC_GAS_STATION_ADDRESS,
  NEXT_PUBLIC_FFT_CAMPAIGN_PACKAGE: process.env.NEXT_PUBLIC_FFT_CAMPAIGN_PACKAGE,
  NEXT_PUBLIC_FFT_CAMPAIGN_ADDR: process.env.NEXT_PUBLIC_FFT_CAMPAIGN_ADDR,
  NEXT_PUBLIC_TRADING_HTTP_URL: process.env.NEXT_PUBLIC_TRADING_HTTP_URL,
  NEXT_PUBLIC_TRADING_WS_URL: process.env.NEXT_PUBLIC_TRADING_WS_URL,
  NEXT_PUBLIC_SENTRY_ENVIRONMENT: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
  NEXT_PUBLIC_GOOGLE_CLIENT_ID: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
  NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
  NEXT_PUBLIC_MAINTENANCE_MODE: process.env.NEXT_PUBLIC_MAINTENANCE_MODE,
  NEXT_PUBLIC_TRADE_MAINTENANCE_MODE: process.env.NEXT_PUBLIC_TRADE_MAINTENANCE_MODE,
  NEXT_PUBLIC_ENCRYPTION_KILLSWITCH: process.env.NEXT_PUBLIC_ENCRYPTION_KILLSWITCH,
  NEXT_PUBLIC_ENCRYPTION_LOCAL_OVERRIDE: process.env.NEXT_PUBLIC_ENCRYPTION_LOCAL_OVERRIDE,
  NEXT_PUBLIC_PRE_DEPOSITS_CAMPAIGN_END_LEDGER_VERSION:
    process.env.NEXT_PUBLIC_PRE_DEPOSITS_CAMPAIGN_END_LEDGER_VERSION,
  NEXT_DEV_COUNTRY_CODE: process.env.NEXT_DEV_COUNTRY_CODE,
  NEXT_DEV_REGION_CODE: process.env.NEXT_DEV_REGION_CODE,
  NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
  NODE_ENV: process.env.NODE_ENV,
  CI: process.env.CI,
  GA_ID: process.env.GA_ID,
  VERCEL_URL: process.env.VERCEL_URL,
  PORT: process.env.PORT,
  NEXT_PUBLIC_MESH_CONNECT_URL: process.env.NEXT_PUBLIC_MESH_CONNECT_URL,
  NEXT_PUBLIC_MESH_CONNECT_CLIENT_ID: process.env.NEXT_PUBLIC_MESH_CONNECT_CLIENT_ID,
  NEXT_PUBLIC_MESH_COINBASE_INTEGRATION_ID: process.env.NEXT_PUBLIC_MESH_COINBASE_INTEGRATION_ID,
  NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY: process.env.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY,
  NEXT_PUBLIC_RUDDERSTACK_DATA_PLANE_URL: process.env.NEXT_PUBLIC_RUDDERSTACK_DATA_PLANE_URL,
  NEXT_PUBLIC_SUI_RPC_URL: process.env.NEXT_PUBLIC_SUI_RPC_URL,
  NEXT_PUBLIC_HIDDEN_MARKET_ADDRS: process.env.NEXT_PUBLIC_HIDDEN_MARKET_ADDRS,
  NEXT_PUBLIC_APP_VERSION: packageJson.version,
  NEXT_PUBLIC_TRADE_ALLOWED_CHECKS_ENABLED: process.env.NEXT_PUBLIC_TRADE_ALLOWED_CHECKS_ENABLED,
  NEXT_PUBLIC_ENABLE_KEYLESS: process.env.NEXT_PUBLIC_ENABLE_KEYLESS,
  NEXT_PUBLIC_ENABLE_MESH_WITHDRAW: process.env.NEXT_PUBLIC_ENABLE_MESH_WITHDRAW,
  NEXT_PUBLIC_ENABLE_AGENT_MODE: process.env.NEXT_PUBLIC_ENABLE_AGENT_MODE,
  NEXT_PUBLIC_ENABLE_IDEAS: process.env.NEXT_PUBLIC_ENABLE_IDEAS,
  NEXT_PUBLIC_ENABLE_RWA_INSIGHTS: process.env.NEXT_PUBLIC_ENABLE_RWA_INSIGHTS,
  NEXT_PUBLIC_ENABLE_MARKET_CONTEXTS_WS: process.env.NEXT_PUBLIC_ENABLE_MARKET_CONTEXTS_WS,
  NEXT_PUBLIC_ENABLE_RWA_AFTER_HOURS: process.env.NEXT_PUBLIC_ENABLE_RWA_AFTER_HOURS,
  // Rpc URLS Override
  NEXT_PUBLIC_SOLANA_RPC_URL: process.env.NEXT_PUBLIC_SOLANA_RPC_URL,
  NEXT_PUBLIC_APTOS_RPC_URL: process.env.NEXT_PUBLIC_APTOS_RPC_URL,
  NEXT_PUBLIC_ARBITRUM_RPC_URL: process.env.NEXT_PUBLIC_ARBITRUM_RPC_URL,
  NEXT_PUBLIC_BASE_RPC_URL: process.env.NEXT_PUBLIC_BASE_RPC_URL,
  NEXT_PUBLIC_ETHEREUM_RPC_URL: process.env.NEXT_PUBLIC_ETHEREUM_RPC_URL,
  NEXT_PUBLIC_ANKR_API_KEY: process.env.NEXT_PUBLIC_ANKR_API_KEY,
  NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID: process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID,
  NEXT_PUBLIC_AUTO_IMPORT_TRANSFERS_ENABLED: process.env.NEXT_PUBLIC_AUTO_IMPORT_TRANSFERS_ENABLED,
  NEXT_PUBLIC_SOLANA_CLAIM_SERVER_URL: process.env.NEXT_PUBLIC_SOLANA_CLAIM_SERVER_URL,
  NEXT_PUBLIC_SIWA_DOMAIN: process.env.NEXT_PUBLIC_SIWA_DOMAIN,
  NEXT_PUBLIC_FUN_API_KEY: process.env.NEXT_PUBLIC_FUN_API_KEY,
  NEXT_PUBLIC_FUN_DEPOSIT_MIN_USD: process.env.NEXT_PUBLIC_FUN_DEPOSIT_MIN_USD,
});

export const isProduction = env.NEXT_PUBLIC_VERCEL_ENV === "production";

/**
 * Whether Fun can run in this build at all. Who may start a checkout is Statsig's `fun_deposits`.
 *
 * An unknown minimum must never degrade to 0: the floor has to clear both Fun's payment-method
 * minimum and NEAR's minimum net Base receipt, and a charge under either is taken from the card
 * and stranded off-Aptos. A minimum under `FUN_MIN_DEPOSIT_FLOOR_USD` fails closed too: the server
 * ignores a payout under $1 as dust. The SDK also throws on mount without a WalletConnect
 * projectId.
 */
export const isFunDepositsConfigured =
  Boolean(env.NEXT_PUBLIC_FUN_API_KEY) &&
  Boolean(env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID) &&
  typeof env.NEXT_PUBLIC_FUN_DEPOSIT_MIN_USD === "number" &&
  Number.isFinite(env.NEXT_PUBLIC_FUN_DEPOSIT_MIN_USD) &&
  env.NEXT_PUBLIC_FUN_DEPOSIT_MIN_USD >= FUN_MIN_DEPOSIT_FLOOR_USD;

export const isMainnet = env.NEXT_PUBLIC_SDK_CONFIG_NAME === "mainnet";
export const isTestnet = env.NEXT_PUBLIC_SDK_CONFIG_NAME === "testnet";
